import { SampleCategories } from '@arpeggi/shared'
import { cast } from 'mobx-state-tree'
import ArpRegistry from '../../features/arp-registry/ArpRegistry'
import { getRandomSampleFromCategoryList } from '../stores/song/sample/SampleUtils'
import { ISongStore } from '../stores/song/SongStore'
import { DrumStemStore } from '../stores/song/stem/DrumStemStore'
import { InstrumentStemStore } from '../stores/song/stem/InstrumentStemStore'
import { StemSettingsStore } from '../stores/song/StemSettingsStore'
import { DrumGenerator } from './DrumGenerator'
import { MelodicGenerator } from './MelodicGenerator'
import { RandIndexWithDistributions, RandProb } from './RandomUtils'

// Scales, for convenience
export const MAJOR_SCALE: number[] = [1, 3, 5, 6, 8, 10, 12, 13]
export const MINOR_SCALE: number[] = [1, 3, 4, 6, 8, 9, 11, 13]

export const genNotesProbabilistically = (
  notes: number[],
  beatProbs: number[],
  noteProbs: number[],
  scaleArray: number[] = MINOR_SCALE
) => {
  for (let i = 1; i < notes.length; i++) {
    // Decide if we should play a note here, based on our fixed probability range.
    if (RandProb(beatProbs[i % beatProbs.length])) {
      // Get random note from note probabilities.
      const note = RandIndexWithDistributions(noteProbs)
      notes[i] = scaleArray[note]
    }
  }
  return notes
}

// Flips the index from 0 to 1 or true to false
export const invert = (array: Array<boolean>, index: number) => {
  array[index] = !array[index]
}

// Checks that the index exists before updating.
export const safeUpdate = (array: number[], index: number, value: number) => {
  if (index >= array.length) return
  array[index] = value
}

export const generateSong = (
  songStore: ISongStore,
  melodicGenerator: MelodicGenerator,
  drumGenerator: DrumGenerator,
  tempo: number,
  melodicStemGenerators: StemGenerator[],
  drumArrangement: number[]
) => {
  songStore.tempo = tempo

  melodicStemGenerators.forEach((generator) => {
    generateMelodicStem(songStore, generator, melodicGenerator)
  })

  generateDrumStem(songStore, drumGenerator, drumArrangement)

  // Make sure all arrangements are filled up to 16.
  songStore.stems.forEach((stem) => {
    for (let i = 0; i < 16; i++) {
      stem.stemSettings.extendArrangement()
    }
  })
}

export const generateMelodicStem = (
  songStore: ISongStore,
  stemGenerator: StemGenerator,
  melodicGenerator: MelodicGenerator
) => {
  const stem = InstrumentStemStore.create()
  // By pushing this here we allow direct access, since it's now connected to the Root MST graph!
  stem.updateMelodicSample(getRandomSampleFromCategoryList(stemGenerator.categories))
  stem.updateStemSettings(StemSettingsStore.create())
  stem.setGain(stemGenerator.defaultGain)

  // Create 2 loops for each
  for (let i = 0; i < 2; i++) {
    stem.addLoop()
    const loop = stem.loops[i].melodicLoop
    if (stemGenerator.type === StemGeneratorType.LEAD) {
      // These sub generators should prob just live in this class?
      melodicGenerator.generateLeadLoop(loop)
    } else if (stemGenerator.type === StemGeneratorType.HARMONY) {
      melodicGenerator.generateHarmonyLoop(loop)
    } else if (stemGenerator.type === StemGeneratorType.BASS) {
      melodicGenerator.generateBassLoop(loop)
    }
  }
  stem.stemSettings.setEntireArrangement(
    stemGenerator.generateArrangement(songStore.songDisplayLength || 16)
  )
  songStore.stems.push(stem)
}

export const generateDrumStem = (
  songStore: ISongStore,
  drumGenerator: DrumGenerator,
  arrangement: number[]
) => {
  const stem = DrumStemStore.create()
  // By pushing this here we allow direct access, since it's now connected to the Root MST graph!
  songStore.stems.push(stem)
  stem.stemSettings = StemSettingsStore.create()
  stem.setGain(0.8)

  // Decide which samples to use
  const subloopTypes = [
    { categories: SampleCategories.Kick, defaultGain: 0.3 },
    { categories: ['Clap'], defaultGain: 0.4 },
    { categories: SampleCategories.Hats, defaultGain: 0.1 },
    { categories: SampleCategories.Percussion, defaultGain: 0.1 }
  ]

  subloopTypes.forEach((subloopType) => {
    stem.drumSamples.push(getRandomSampleFromCategoryList(subloopType.categories))
    stem.drumGains.push(subloopType.defaultGain)
  })
  // Create 2 loops and arrange them
  for (let i = 0; i < 2; i++) {
    stem.addLoop()
    const loop = stem.loops[i].drumLoop
    loop.subLoops.forEach((subLoop, subLoopIndex) => {
      // TODO could leave a subloop empty for intro sections.
      const sample = stem.drumSamples[subLoopIndex]
      const category = ArpRegistry.retrieveFromCache(sample.sampleIndex)?.category
      drumGenerator.generateDrumSubloopForCategory(subLoop, category)
    })
  }
  stem.stemSettings.arrangement = cast(arrangement)
}

export enum StemGeneratorType {
  LEAD,
  HARMONY,
  BASS
}

export type StemGenerator = {
  type: StemGeneratorType
  categories: string[]
  generateArrangement: (number: number) => number[]
  defaultGain: number
}
